// ─── Utilidades del programa del evento ──────────────────────────────────────
// Un mismo cálculo de horas se usa en el panel de administración y en la
// página pública, así que vive aquí para que nunca se desincronicen.

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

// "2026-11-13" -> "Viernes 13 de noviembre". Se construye la fecha en UTC para
// que el día no se corra una jornada según la zona horaria del navegador.
export const dayTitleFromDate = (isoDate) => {
  if (!isoDate) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) return '';
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (Number.isNaN(date.getTime())) return '';
  return capitalize(
    `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} de ${MONTHS[date.getUTCMonth()]}`,
  );
};

// El título guardado manda; si está vacío se deriva de la fecha.
export const dayLabel = (day) => {
  if (!day) return '';
  const title = (day.title || '').trim();
  return title || dayTitleFromDate(day.day_date) || 'Día sin título';
};

// Postgres devuelve "14:00:00"; el <input type="time"> quiere "14:00".
export const toTimeInputValue = (time) => {
  if (!time) return '';
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time).trim());
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const timeToMinutes = (time) => {
  const value = toTimeInputValue(time);
  if (!value) return null;
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (totalMinutes) => {
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  const hours = String(Math.floor(normalized / 60)).padStart(2, '0');
  const minutes = String(normalized % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const endTimeOf = (startTime, durationMinutes) => {
  const start = timeToMinutes(startTime);
  const duration = Number(durationMinutes);
  if (start === null || !Number.isFinite(duration) || duration <= 0) return '';
  return minutesToTime(start + duration);
};

// "14:00 – 15:30" (o solo la hora de inicio si no hay duración válida).
export const formatTimeRange = (startTime, durationMinutes) => {
  const start = toTimeInputValue(startTime);
  if (!start) return '';
  const end = endTimeOf(startTime, durationMinutes);
  return end ? `${start} – ${end}` : start;
};

// 90 -> "1 h 30 min"
export const formatDuration = (durationMinutes) => {
  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration) || duration <= 0) return '';
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (!hours) return `${minutes} min`;
  if (!minutes) return `${hours} h`;
  return `${hours} h ${minutes} min`;
};

const startSortValue = (event) => {
  const minutes = timeToMinutes(event?.start_time);
  return minutes === null ? Number.MAX_SAFE_INTEGER : minutes;
};

export const sortEvents = (events = []) =>
  [...events].sort(
    (a, b) =>
      startSortValue(a) - startSortValue(b) ||
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      String(a.title || '').localeCompare(String(b.title || '')),
  );

export const sortDays = (days = []) =>
  [...days].sort(
    (a, b) =>
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      String(a.day_date || '').localeCompare(String(b.day_date || '')) ||
      String(a.created_at || '').localeCompare(String(b.created_at || '')),
  );

// Agrupa las actividades de un día en bloques listos para renderizar:
//  - { type: 'activity', event }
//  - { type: 'workshops', startTime, durationMinutes, events } para los talleres
//    que comparten hora de inicio (son simultáneos).
export const buildDayBlocks = (events = []) => {
  const ordered = sortEvents(events);
  const blocks = [];
  const workshopBlocksByKey = new Map();

  ordered.forEach((event) => {
    if (!event.is_workshop) {
      blocks.push({ type: 'activity', key: `activity-${event.id}`, event });
      return;
    }

    const key = toTimeInputValue(event.start_time) || `sin-hora-${event.id}`;
    const existing = workshopBlocksByKey.get(key);
    if (existing) {
      existing.events.push(event);
      existing.durationMinutes = Math.max(
        existing.durationMinutes || 0,
        Number(event.duration_minutes) || 0,
      );
      return;
    }

    const block = {
      type: 'workshops',
      key: `workshops-${key}`,
      startTime: event.start_time,
      durationMinutes: Number(event.duration_minutes) || 0,
      events: [event],
    };
    workshopBlocksByKey.set(key, block);
    blocks.push(block);
  });

  return blocks;
};

// [{ day, blocks, events }] — solo los días que tienen actividades quedan
// disponibles para la vista pública; el admin decide si los muestra vacíos.
export const groupScheduleByDay = (days = [], events = []) => {
  const eventsByDay = new Map();
  events.forEach((event) => {
    if (!event.day_id) return;
    if (!eventsByDay.has(event.day_id)) eventsByDay.set(event.day_id, []);
    eventsByDay.get(event.day_id).push(event);
  });

  return sortDays(days).map((day) => {
    const dayEvents = sortEvents(eventsByDay.get(day.id) || []);
    return { day, events: dayEvents, blocks: buildDayBlocks(dayEvents) };
  });
};

// Hora de inicio y fin del día completo, para el encabezado de cada sección.
export const dayTimeSpan = (events = []) => {
  const withTime = events.filter((event) => toTimeInputValue(event.start_time));
  if (!withTime.length) return '';
  const starts = withTime.map((event) => timeToMinutes(event.start_time));
  const ends = withTime.map(
    (event) => timeToMinutes(event.start_time) + (Number(event.duration_minutes) || 0),
  );
  return `${minutesToTime(Math.min(...starts))} – ${minutesToTime(Math.max(...ends))}`;
};
